import { Injectable, inject, signal, effect } from '@angular/core';
import { StorageKeys } from '@core/utils/storage-keys.util';
import { AlertDialogService } from '@dialogs/alert-dialog/alert-dialog.service';
import { StorageService } from '@services/storage/storage.service';
import { UserService } from '../user/user.service';
import { differenceInDays } from 'date-fns';
import { PusherService, PUSHER_CHANNEL_KEY, PUSHER_EVENT } from '../pusher/pusher.service';

@Injectable({
  providedIn: 'root',
})
export class PwaService {
  private readonly pusherService = inject(PusherService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly storageService = inject(StorageService);
  private readonly userService = inject(UserService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  installPrompt = signal<any>(null);
  private updateEventSubscribed = false;

  private readonly userEffect = effect(() => {
    const user = this.userService.user();
    if (user && !this.updateEventSubscribed) {
      this.handleUpdateEvent();
      this.updateEventSubscribed = true;
    }
  });

  initialize(shouldShowPrompt?: () => boolean): void {
    this.handleInstallPrompt(shouldShowPrompt);
  }

  private handleInstallPrompt(shouldShowPrompt?: () => boolean): void {
    window.addEventListener('beforeinstallprompt', e => {
      this.installPrompt.set(e);

      if (this.installPrompt()) {
        if (shouldShowPrompt && !shouldShowPrompt()) {
          return;
        }

        const lastPromptDate = this.storageService.getItem<string>(StorageKeys.PWA_PROMPT_DATE);

        if (!lastPromptDate) {
          this.showPWAPrompt();
          return;
        }

        const lastDate = new Date(lastPromptDate);
        const today = new Date();
        const daysDifference = differenceInDays(today, lastDate);

        if (daysDifference >= 30) {
          this.showPWAPrompt();
        }
      }
    });
  }

  private async handleUpdateEvent(): Promise<void> {
    try {
      const updateSubject = await this.pusherService.subscribeEvent(
        PUSHER_CHANNEL_KEY.Deployments,
        PUSHER_EVENT.DeploymentNew
      );
      updateSubject.subscribe(() => {
        this.openRefreshDialog();
      });
    } catch (error) {
      console.error('Error subscribing to update event:', error);
    }
  }

  private openRefreshDialog(): void {
    this.alertDialogService.open({
      title: 'Theres a new update',
      message:
        'We have a new update coming to make your work better. We will refresh the site to make way for new things!',
      primaryAction: {
        label: 'Refresh',
        handler: async () => {
          window.location.reload();
          Promise.resolve();
        },
      },
      disableClose: true,
    });
  }

  private updateLastPromptDate(): void {
    const today = new Date().toISOString();
    this.storageService.setItem(StorageKeys.PWA_PROMPT_DATE, today);
  }

  private async showPWAPrompt(): Promise<void> {
    const res = await this.alertDialogService.open({
      maxWidth: '700px',
      title: 'Do you want to add it to your Home Screen?',
      message:
        'Now you can view to this site directly from your Home Screen. This way is easier to access the platform. Do you want to continue?',
      desktopTitle: 'Do you want to add it to your Home Screen?',
      desktopMessage: `Now you can view to this site directly from your Home Screen. This way is easier to access the platform. Do you want to continue?`,
      primaryAction: {
        label: 'Yes, continue!',
        handler: async () => {
          await this.installPWA();
        },
      },
      secondaryAction: {
        label: 'Maybe Later',
        handler: async () => {
          this.updateLastPromptDate();
        },
        variant: 'secondary',
      },
    });

    if (!res) {
      this.updateLastPromptDate();
    }
  }

  async installPWA(): Promise<void> {
    if (!this.installPrompt()) return;

    await this.installPrompt().prompt();
  }
}
